import { ChangeDetectionStrategy, Component, computed, input, signal, inject } from '@angular/core';
import { Icon, IconComponent } from '@circlek-global/lds-components';

import { GtmCustomEventNameEnum } from '~components/gtm-script/gtm-custom-event-name.enum';
import { GtmCustomEventService } from '~components/gtm-script/gtm-custom-event.service';
import type { OpeningHours } from '~models/station.model';
import { TranslatePipe } from '~pipes/translate.pipe';

interface DayData {
  key: string;
  isOpen: boolean;
  hours: string;
  isCurrentDay: boolean;
  textColorClass: string;
}

@Component({
  selector: 'app-opening-hours',
  imports: [IconComponent, TranslatePipe],
  templateUrl: './opening-hours.component.html',
  styles: `
    .current-day {
      @apply font-semibold;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpeningHoursComponent {
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  readonly openingHours = input.required<OpeningHours>();
  readonly Icon = Icon;
  readonly isExpanded = signal(false);
  private readonly weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  private readonly currentDayIndex = computed(() => {
    const today = new Date().getDay();

    // Convert Sunday (0) to index 6, and shift others down by 1
    return today === 0 ? 6 : today - 1;
  });
  readonly daysData = computed((): DayData[] => {
    const currentIdx = this.currentDayIndex();
    const openingHours = this.openingHours();

    return this.weekdays.map((day, index) => {
      const dayHours = openingHours[day];
      const isOpen = !!dayHours;

      return {
        key: day,
        isOpen,
        hours: dayHours ? `${dayHours.open} - ${dayHours.close}` : '',
        isCurrentDay: index === currentIdx,
        textColorClass: isOpen ? 'text-gray-10' : 'text-gray-60',
      };
    });
  });

  toggleExpanded(): void {
    this.isExpanded.update((val) => !val);

    if (this.isExpanded()) {
      this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.HoursDrop);
    }
  }
}
